import React, { useRef, useState } from 'react'

import {
  Avatar,
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Icon,
  Table,
  Tag
} from 'antd'
import { getAllLOMAUsers } from '../../modules/loma'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import { LOMAUSERS_STATUS, ENDPOINTLOMA } from '../../utils/constants'
import { useDeepEffect } from '../../utils/hooks'
import moment from 'moment'
import Highlighter from 'react-highlight-words'
import Lightbox from 'react-image-lightbox'
import '../../../node_modules/react-image-lightbox/style.css'
import { ButtonEx } from '../../styles/button.style'

const GetStatusColor = (status) => {
  let color = ''
  if (status !== null) {
    switch (status.toUpperCase()) {
      case 'ACTIVE':
        color = 'green'
        break
      case 'INACTIVE':
        color = 'red'
        break
      default:
        break
    }
  }
  return color
}

const LOMAUsers = ({ form }) => {
  const dispatch = useDispatch()
  let searchInput = useRef(null)
  const [searchText, setSearchText] = useState('')
  const [current, setCurrent] = useState('')

  const { userList, isLoading } = useSelector(
    (state) => ({
      userList: state.loma.userList,
      isLoading: state.loma.isLoading
    }),
    []
  )

  async function fetch() {
    await dispatch(getAllLOMAUsers())
  }

  useDeepEffect(() => {
    fetch()
  }, [])

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={(node) => {
            searchInput = node
          }}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm)}
          icon="search"
          size="small"
          style={{ width: 90, marginRight: 8 }}>
          Search
        </Button>
        <Button
          onClick={() => handleReset(clearFilters)}
          size="small"
          style={{ width: 90 }}>
          Reset
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <Icon type="search" style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.select())
      }
    },
    render: (text) => (
      <Highlighter
        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
        searchWords={[searchText]}
        autoEscape
        textToHighlight={text && text.toString()}
      />
    )
  })

  const handleSearch = (selectedKeys, confirm) => {
    confirm()
    setSearchText(selectedKeys[0])
  }

  const handleReset = (clearFilters) => {
    clearFilters()
    setSearchText('')
  }

  const handleClickImage = (e, image) => {
    e && e.preventDefault()
    setCurrent(image)
  }

  const handleCloseModal = (e) => {
    e && e.preventDefault()
    setCurrent('')
  }

  const onClickToHero = (e, record) => {
    e.preventDefault()
    const id = record.heroId // สมมติว่า id ถูกเก็บไว้ใน property ชื่อ id ของ record
    window.location.href =
      process.env.REACT_APP_WEB_ENDPOINT + `/heroes?id=${id}`
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60
    },
    {
      title: 'วันที่เปิดใช้งาน',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      sorter: (a, b) => a.createdAt - b.createdAt,
      render: (createdAt) => {
        const created = GetDate(createdAt)
        return <span>{created}</span>
      }
    },
    {
      title: 'วันที่ใช้งานล่าสุด',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 150,
      sorter: (a, b) => a.updatedAt - b.updatedAt,
      render: (updatedAt) => {
        const updated = GetDate(updatedAt)
        return <span>{updated}</span>
      }
    },
    {
      title: 'ชื่อผู้ใช้งาน',
      dataIndex: 'username',
      key: 'username',
      width: 110,
      ...getColumnSearchProps('username')
    },
    {
      title: 'ชื่อสาขา',
      dataIndex: 'branchName',
      key: 'branchName',
      width: 180,
      ...getColumnSearchProps('branchName')
    },
    {
      title: 'ชื่อร้าน',
      dataIndex: 'heroName',
      key: 'heroName',
      width: 220,
      ...getColumnSearchProps('heroName'),
      render: (name, record) => (
        <Row type="flex" gutter={8}>
          <Col>
            <div>
              <Avatar
                src={record.image}
                onClick={(e) => handleClickImage(e, record.image)}
              />
              {current && (
                <Lightbox mainSrc={current} onCloseRequest={handleCloseModal} />
              )}
            </div>
          </Col>
          <Col>
            <a
              onClick={(e) => {
                onClickToHero(e, record)
              }}>
              {name}
            </a>
          </Col>
        </Row>
      )
    },
    {
      title: 'บิลวันนี้',
      dataIndex: 'billsToday',
      key: 'billsToday',
      width: 100,
      sorter: (a, b) => a.billsToday - b.billsToday
    },
    {
      title: 'บิลเมื่อวาน',
      dataIndex: 'billsYesterday',
      key: 'billsYesterday',
      width: 120,
      sorter: (a, b) => a.billsYesterday - b.billsYesterday
    },
    {
      title: 'บิล 7 วันล่าสุด',
      dataIndex: 'billsLastWeek',
      key: 'billsLastWeek',
      width: 135,
      sorter: (a, b) => a.billsLastWeek - b.billsLastWeek
    },
    {
      title: 'บิล 30 วันล่าสุด',
      dataIndex: 'billsLastMonth',
      key: 'billsLastMonth',
      width: 120,
      sorter: (a, b) => a.billsLastMonth - b.billsLastMonth
    },
    {
      title: 'บิลทั้งหมด',
      dataIndex: 'billsAllTime',
      key: 'billsAllTime',
      width: 115,
      sorter: (a, b) => a.billsAllTime - b.billsAllTime
    },
    {
      title: 'เครื่องที่ใช้งาน',
      dataIndex: 'deviceAvailable',
      key: 'deviceAvailable',
      width: 140,
      sorter: (a, b) => a.deviceAvailable - b.deviceAvailable
    },
    {
      title: 'สาขาที่ใช้งาน',
      dataIndex: 'branchAvailable',
      key: 'branchAvailable',
      width: 140,
      sorter: (a, b) => a.branchAvailable - b.branchAvailable
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      filters: LOMAUSERS_STATUS.map((status) => ({
        text: status,
        value: status
      })),
      onFilter: (value, record) => record.status === value,
      render: (status) => {
        const color = GetStatusColor(status)
        return (
          <span>
            <Tag color={color} key={status}>
              {status}
            </Tag>
          </span>
        )
      }
    }
  ]

  const GetDate = (created) => {
    let date = ''
    if (created !== null) {
      date = moment(created).add(543, 'years').format('DD/MM/YYYY')
    }
    return date
  }

  return (
    <Row type="flex" gutter={[8, 8]}>
      <Col span={24}>
        <Row type="flex" justify="end">
          <Col>
            <ButtonEx color="#6ebf4b" type="primary">
              <a
                href={ENDPOINTLOMA + `/users/admin/getAllLOMAUser/export`}
                download>
                Export Data
              </a>
            </ButtonEx>
          </Col>
        </Row>
      </Col>
      <Col span={24}>
        <Card
          title="LOMA Users"
          extra={
            <div>
              <Button size="small" icon="reload" onClick={() => fetch()}>
                Reload
              </Button>
            </div>
          }>
          {!isEmpty(userList) && (
            <Table
              loading={isLoading}
              scroll={{ x: 700 }}
              dataSource={userList}
              columns={columns}
              pagination={{
                pageSizeOptions: ['10', '20', '50', '100'],
                showSizeChanger: true
              }}
            />
          )}
        </Card>
      </Col>
    </Row>
  )
}

const WrappedLOMAUsers = Form.create({ name: 'LOMAUsers' })(LOMAUsers)
export default WrappedLOMAUsers
